/**
 * 018: structure copied from ../invoicing/List.tsx (loader reads page/date-range/status from the
 * URL, `PagedTable` owns the frame, a status dropdown whose counts are per-status `pageSize: 1`
 * reads under the same date filter) — same pattern, applied to Billing's payments list (FIN-05,
 * GAP-8). `DateFiled` from/to pair copied from ../orders/filters.tsx.
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { PiArrowRightBold as ArrowRight } from "react-icons/pi";
import { Button } from "rizzui";
import { EmptyState, ErrorSurface } from "@steelyard/theme";
import { FILTER_LABEL } from "../../components/table/toolbar";
import { DateField } from "../../components/date-field";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { paymentColumns } from "./payment-columns";
import { listPayments, PAYMENT_STATUSES, readPayment } from "../../lib/services/finance";
import { hrefWith, readPaging } from "../../lib/paging";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";
import { parseIsoDate, toIsoDate } from "../../lib/format";


export async function paymentsListLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const status = params.get("status") ?? undefined;
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;

  // One status's own badge is worth more than a raw wire word, so each status count is read next
  // to the page itself — same "pageSize: 1, same other filters" idiom as ../invoicing/List.tsx's
  // stateTotals, never derived from the shown page.
  const [result, ...statusTotals] = await Promise.all([
    listPayments({ page, pageSize, from, to, status }),
    ...PAYMENT_STATUSES.map((option) =>
      listPayments({ page: 1, pageSize: 1, from, to, status: option })
        .then((counted) => counted.totalCount)
        .catch(() => null),
    ),
  ]);

  return { result, statusTotals, status, from, to };
}

export default function PaymentsList() {
  const { result, statusTotals, status, from, to } = useLoaderData() as Awaited<ReturnType<typeof paymentsListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  if (!can(permissionState, "billing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view payments" />;
  }

  const query = (over: Record<string, string | undefined>) => hrefWith("/finance/payments", searchParams, over);
  const filtered = Boolean(status || from || to);

  const statusOptions: StatusOption[] = [
    { label: "Every status", href: query({ status: undefined, page: undefined }), active: !status },
    ...PAYMENT_STATUSES.map((option, index) => ({
      label: readPayment(option).heading,
      href: query({ status: option, page: undefined }),
      active: option === status,
      count: statusTotals[index],
      status: option,
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Payments"
        count={result.totalCount}
        description="Every payment attempt Billing has recorded, with the reference that matches it to the provider's own statement."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Finance" }, { name: "Payments" }]}
      />

      <PagedTable
        actions={
          <Link to="/finance/refunds">
            <Button as="span" variant="outline">
              Outstanding refunds
              <ArrowRight aria-hidden className="ms-1.5 size-3.5" />
            </Button>
          </Link>
        }
        result={result}
        caption="Payments, with status and reconciliation reference"
        columns={paymentColumns}
        getRowId={(row) => row.paymentId}
        status={{ label: "Status", options: statusOptions }}
        filters={
          <>
            {/* Two independent pickers, not one range field — see orders/filters.tsx for why:
                `suffix: null` drops the dropdown-caret icon (the calendar prefix is the only
                "open" affordance), and clearing is rizzui's own `clearable`/`onClear`, not
                react-datepicker's `isClearable` (a hard-coded blue circle that doesn't match this
                app's inputs). */}
            <DateField
              dateFormat="dd-MMM-yyyy"
              className="w-full @5xl:w-48"
              placeholderText="Initiated from…"
              selected={parseIsoDate(from ?? null)}
              maxDate={parseIsoDate(to ?? null) ?? undefined}
              onChange={(date) =>
                navigate(query({ from: date ? toIsoDate(date as Date) : undefined, page: undefined }))
              }
              inputProps={{
                label: "Initiated from",
                labelClassName: FILTER_LABEL,
                suffix: null,
                clearable: Boolean(from),
                onClear: () => navigate(query({ from: undefined, page: undefined })),
              }}
            />
            <DateField
              dateFormat="dd-MMM-yyyy"
              className="w-full @5xl:w-48"
              placeholderText="Initiated to…"
              selected={parseIsoDate(to ?? null)}
              minDate={parseIsoDate(from ?? null) ?? undefined}
              onChange={(date) =>
                navigate(query({ to: date ? toIsoDate(date as Date) : undefined, page: undefined }))
              }
              inputProps={{
                label: "Initiated to",
                labelClassName: FILTER_LABEL,
                suffix: null,
                clearable: Boolean(to),
                onClear: () => navigate(query({ to: undefined, page: undefined })),
              }}
            />
          </>
        }
        clear={{ active: filtered, href: query({ status: undefined, from: undefined, to: undefined, page: undefined }) }}
        empty={
          <EmptyState
            title="No payments matched"
            action={
              filtered ? (
                <Link to="/finance/payments">
                  <Button as="span" size="sm" variant="outline">
                    Clear the filters
                  </Button>
                </Link>
              ) : null
            }
          >
            {filtered ? "No payment in this period is in that status." : "No payment has been taken yet."}
          </EmptyState>
        }
      />
    </div>
  );
}
